import styles from "./ChatBox.module.css";
import {
  ACCEPTED_EXTENSIONS,
  ACCEPTED_EXTENSIONS_LABEL,
  MAX_DOCS_PER_SESSION,
  admitFiles,
  notifyAdmissionWarnings
} from "@lib/agent/chatAttachments";
import type { CommittedDocuments } from "@lib/agent/types";
import Icon from "@lib/common/components/Icon/Icon";
import ChatAttachmentChips, { type ChatAttachmentChipItem } from "@lib/experiment/agent/ChatAttachmentChips";
import type { DraftReport } from "@lib/research/AttachReportModalContent";
import type { PaperProps, TextareaProps } from "@mantine/core";
import { Button, Group, Paper, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import { useField } from "@mantine/form";
import { isString } from "lodash-es";
import type { ChangeEvent, KeyboardEvent, Ref } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const MAX_MESSAGE_LENGTH = 10_000;

export type ChatBoxSubmitPayload = {
  // Raw files held client-side, uploaded by `onSubmit` at send time. The parent owns the list and
  // clears it once the send succeeds. Display names for the sent-message chips come from the
  // upload response (authoritative filenames), not from these File objects.
  files?: File[];
  // Message-scoped research reports staged in the composer; ids travel with the WS prompt.
  reports?: DraftReport[];
};

// Called on send. The files are uploaded here, before the message is dispatched. Return `false`
// to keep the draft (text + files) — e.g. an upload failed; a `void` or `true` return clears it.
export type ChatBoxOnSubmit = (
  message: string,
  payload: ChatBoxSubmitPayload
) => boolean | void | Promise<boolean | void>;

export default function ChatBox({
  ref,
  onSubmit,
  onCancel,
  disabled = false,
  isUploading = false,
  isStreaming = false,
  isStopping = false,
  inputSuggestion,
  textareaProps = {},
  paperProps,
  isFileUploadEnabled = false,
  isDeepResearchEnabled = false,
  selectedFiles,
  onFilesChange,
  selectedReports,
  onReportsChange,
  onAttachClick,
  onAttachReportClick,
  committedDocuments = { count: 0, totalBytes: 0 },
  composerSeed,
  onComposerSeedConsumed
}: {
  ref: Ref<HTMLTextAreaElement>;
  onSubmit: ChatBoxOnSubmit;
  onCancel?: () => void;
  disabled?: boolean;
  /** True while session documents are uploading (bootstrap handoff or compose path). */
  isUploading?: boolean;
  isStreaming?: boolean;
  // True once Stop is clicked, until the turn settles. Shows the Stop button's spinner and disables
  // it so the cooperative-cancel latency window can't be spammed.
  isStopping?: boolean;
  inputSuggestion?: string | null;
  textareaProps?: TextareaProps;
  paperProps?: Partial<PaperProps>;
  isFileUploadEnabled?: boolean;
  isDeepResearchEnabled?: boolean;
  // The parent owns the raw `File[]` (not yet uploaded); submit forwards them so the parent can
  // upload at send time.
  selectedFiles?: File[];
  onFilesChange?: (files: File[]) => void;
  selectedReports?: DraftReport[];
  onReportsChange?: (reports: DraftReport[]) => void;
  onAttachClick?: () => void;
  onAttachReportClick?: () => void;
  // Documents already committed to the session — count caps the picker slots; totalBytes feeds the
  // cumulative size hint. Both are soft hints: the upload endpoint stays authoritative.
  committedDocuments?: CommittedDocuments;
  /** One-shot message prefill (e.g. after a failed dashboard handoff upload). */
  composerSeed?: string;
  onComposerSeedConsumed?: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // True while `onSubmit` runs — uploading the draft files happens before the message is sent, which
  // can take seconds for a large PDF, so the composer shows a pending state and blocks re-entry.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // True while a send/upload is in flight, from either source — drives every "busy" concern below
  // (re-entry guard, attach/submit disabling, and the dimmed cursor-progress compose area).
  const isBusy = isSubmitting || isUploading;

  const field = useField({
    initialValue: "",
    validateOnChange: true,
    validate: value => (value.length > MAX_MESSAGE_LENGTH ? "Message too long" : null)
  });
  // Destructure without error — we render our own counter instead of Mantine's default error UI
  const { value: inputValue, onChange: fieldOnChange, onBlur } = field.getInputProps();
  const isOverLimit = !!field.error;

  // Parent owns draft files / reports when both the list and the setter are provided.
  const isFileDeferMode = selectedFiles !== undefined && onFilesChange !== undefined;
  const isReportDeferMode = selectedReports !== undefined && onReportsChange !== undefined;
  // Stabilize the fallback empty array so callbacks depending on it aren't re-created every render.
  const deferredFiles = useMemo(() => selectedFiles ?? [], [selectedFiles]);
  const deferredReports = useMemo(() => selectedReports ?? [], [selectedReports]);

  const hasInputSuggestion = useMemo(() => isString(inputSuggestion) && inputSuggestion.length > 0, [inputSuggestion]);

  useEffect(() => {
    if (!composerSeed) {
      return;
    }
    field.setValue(composerSeed);
    onComposerSeedConsumed?.();
  }, [composerSeed, field, onComposerSeedConsumed]);

  const submit = useCallback(async () => {
    // The Enter key calls submit() directly, bypassing the disabled Send button, so re-entry is
    // blocked here: a second send while the first is still uploading would commit the files twice.
    if (isBusy) {
      return;
    }
    const message = field.getValue().trim();
    if (!message || isOverLimit) {
      return;
    }
    // Bail when the send would no-op anyway — `disabled` reflects history loading/errored, and
    // `isStreaming` means a turn is in flight — so we don't clear the typed text without sending.
    if (disabled || isStreaming) {
      return;
    }

    setIsSubmitting(true);
    let result: boolean | void;
    try {
      result = await onSubmit(message, {
        files: isFileDeferMode ? deferredFiles : undefined,
        reports: isReportDeferMode && deferredReports.length > 0 ? deferredReports : undefined
      });
    } catch {
      // A thrown send (e.g. a failed upload) keeps the draft so the user can retry without retyping.
      result = false;
    } finally {
      setIsSubmitting(false);
    }

    // Clear only when the send wasn't rejected. A `void`-returning onSubmit (no upload step) still
    // clears; an explicit `false` or a throw keeps the text and drafts so nothing is lost.
    if (result !== false) {
      field.setValue("");
      if (isFileDeferMode) {
        onFilesChange([]);
      }
      if (isReportDeferMode) {
        onReportsChange([]);
      }
    }
  }, [
    isBusy,
    field,
    isOverLimit,
    disabled,
    isStreaming,
    onSubmit,
    isFileDeferMode,
    isReportDeferMode,
    deferredFiles,
    deferredReports,
    onFilesChange,
    onReportsChange
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey && !isStreaming) {
        e.preventDefault();
        void submit();
      } else if (e.key === "Tab" && hasInputSuggestion && field.getValue() === "") {
        e.preventDefault();
        // Auto-insert inputSuggestion on Tab.
        field.setValue((inputSuggestion ?? "").replace("e.g. ", ""));
        e.currentTarget.focus();
      }
    },
    [isStreaming, submit, hasInputSuggestion, field, inputSuggestion]
  );

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const incoming = Array.from(e.target.files ?? []);
      // Reset the input so the same file can be re-picked after removal.
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (incoming.length === 0) {
        return;
      }

      // Mirror the per-file size, per-session count, and cumulative total-size caps locally for
      // immediate feedback; the upload at send time stays authoritative. `committedDocuments`
      // accounts for documents already committed to the session so the draft can't push the session
      // over the caps.
      const deferredBytes = deferredFiles.reduce((sum, f) => sum + f.size, 0);
      const admission = admitFiles(incoming, {
        existingCount: committedDocuments.count + deferredFiles.length,
        existingBytes: committedDocuments.totalBytes + deferredBytes
      });
      notifyAdmissionWarnings(admission);
      if (admission.accepted.length > 0 && onFilesChange) {
        onFilesChange([...deferredFiles, ...admission.accepted]);
      }
    },
    [committedDocuments, deferredFiles, onFilesChange]
  );

  const chipItems: ChatAttachmentChipItem[] = [
    ...deferredFiles.map((file, index) => ({
      key: `file:${index}`,
      name: file.name,
      kind: "document" as const,
      onRemove: onFilesChange ? () => onFilesChange(deferredFiles.filter((_, i) => i !== index)) : undefined
    })),
    ...deferredReports.map(report => ({
      key: `report:${report.id}`,
      name: report.title,
      kind: "report" as const,
      onRemove: onReportsChange ? () => onReportsChange(deferredReports.filter(r => r.id !== report.id)) : undefined
    }))
  ];

  const remainingSlots = MAX_DOCS_PER_SESSION - committedDocuments.count - deferredFiles.length;
  // Block attaching while a send is in flight: that send already captured the draft files and
  // clears them on success, so a file added mid-send would be dropped without being uploaded.
  const canAttachMore = remainingSlots > 0 && !isBusy;
  const submitDisabled = disabled || !inputValue.trim() || isOverLimit || isBusy;
  const showCounter = inputValue.length >= MAX_MESSAGE_LENGTH - 500;
  const placeholder = useMemo(() => {
    if (hasInputSuggestion) {
      return inputSuggestion + "  (press Tab to use suggestion)";
    }
    return textareaProps.placeholder ?? "";
  }, [hasInputSuggestion, inputSuggestion, textareaProps.placeholder]);

  return (
    <Paper
      classNames={{ root: styles.textareaPaperWrapper }}
      shadow="xxs"
      px="sm"
      pt={5}
      pb="xs"
      withBorder
      w="100%"
      radius="md"
      {...paperProps}
    >
      <Stack gap="xs" align="space-between" h="100%">
        <Stack gap="xs" className={isBusy ? styles.composeBusyContent : undefined}>
          {chipItems.length > 0 && (
            // Draft files/reports aren't committed yet — removing a chip only edits the local selection.
            <ChatAttachmentChips items={chipItems} />
          )}
          <Textarea
            ref={ref}
            {...textareaProps}
            placeholder={placeholder}
            value={inputValue}
            // `readOnly` (not `disabled`) keeps the text fully legible; the dimmed wrapper above is
            // what actually signals "busy" — `submit()` already re-guards against re-entry.
            readOnly={isBusy}
            // Compose handlers to ensure both internal state and any external
            // onChange (e.g., typewriter animation control) are called
            onChange={e => {
              fieldOnChange(e);
              textareaProps.onChange?.(e);
            }}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            size="md"
            autoFocus
            data-autofocus
            autosize={textareaProps.autosize ?? true}
            minRows={textareaProps.minRows ?? 1}
            maxRows={textareaProps.maxRows ?? 6}
            variant="unstyled"
            radius="md"
            fw={500}
          />
        </Stack>

        <Group justify="space-between" align="flex-end">
          <Group gap="xs">
            {isFileUploadEnabled && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_EXTENSIONS}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <Tooltip
                  label={
                    canAttachMore
                      ? `Attach documents (${ACCEPTED_EXTENSIONS_LABEL})`
                      : `Document limit reached (${MAX_DOCS_PER_SESSION}/session)`
                  }
                >
                  <Button
                    color="gray"
                    variant="subtle"
                    size="sm"
                    miw="unset"
                    px="xs"
                    onClick={() => {
                      onAttachClick?.();
                      fileInputRef.current?.click();
                    }}
                    disabled={!canAttachMore}
                    loading={isUploading}
                  >
                    <Icon name="paperclip" />
                  </Button>
                </Tooltip>
              </>
            )}
            {isDeepResearchEnabled && onAttachReportClick && (
              <Tooltip label="Attach report">
                <Button color="gray" variant="subtle" size="sm" miw="unset" px="xs" onClick={onAttachReportClick}>
                  <Icon name="file-lines" />
                </Button>
              </Tooltip>
            )}
          </Group>
          <Group gap="xs" align="flex-end">
            {showCounter && (
              <Text size="xs" c={isOverLimit ? "red" : "dimmed"}>
                {inputValue.length.toLocaleString()}/{MAX_MESSAGE_LENGTH.toLocaleString()}
              </Text>
            )}
            {isStreaming ? (
              <Button
                color="gray"
                variant="light"
                size="xs"
                miw="unset"
                px="xs"
                onClick={onCancel}
                loading={isStopping}
                disabled={isStopping}
              >
                <Icon name="stop" />
              </Button>
            ) : (
              <Button
                color="var(--mantine-primary-color-filled)"
                variant="filled"
                size="xs"
                miw="unset"
                px="xs"
                disabled={submitDisabled}
                loading={isBusy}
                onClick={() => void submit()}
              >
                <Icon name="arrow-up" />
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Paper>
  );
}
